import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Search, Files, Edit2, Trash2, X, Save, Copy, Package
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  getTemplates, createTemplate, updateTemplate, deleteTemplate, 
  getProducts, getEntities, formatMoney, getApiError 
} from '../api';
import { useLanguage } from '../i18n';

const CATEGORIES = ['Knygomatai', 'Paslaugos', 'Grąžinimo dėžės', 'Detektoriai', 'Kita'];

const QdTemplates = () => {
  const { t, language: uiLang } = useLanguage();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  const user = JSON.parse(localStorage.getItem('qd_user') || '{}');
  const canEdit = ['admin', 'superadmin'].includes(user.role);
  
  useEffect(() => {
    loadTemplates();
  }, []);
  
  const loadTemplates = async () => {
    try {
      const response = await getTemplates();
      setTemplates(response.data);
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async (template) => {
    const confirmMsg = uiLang === 'lt' 
      ? `Ar tikrai norite ištrinti šabloną "${template.name}"?`
      : `Are you sure you want to delete template "${template.name}"?`;
    if (!confirm(confirmMsg)) return;
    
    try {
      await deleteTemplate(template.id);
      toast.success(t('templates.messages.deleted'));
      loadTemplates();
    } catch (error) {
      toast.error(getApiError(error));
    }
  };
  
  const handleUseTemplate = (template) => {
    navigate('/quote/quotes/new', { state: { template } });
  };
  
  const filteredTemplates = templates.filter(template => {
    if (!search) return true;
    return template.name?.toLowerCase().includes(search.toLowerCase());
  });
  
  const calculateTotal = (items) => {
    return items.reduce((sum, item) => sum + (item.total || 0), 0);
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('templates.title')}</h1>
          <p className="text-gray-500">
            {filteredTemplates.length} {uiLang === 'lt' ? 'šablonų' : 'templates'}
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => { setEditingTemplate(null); setShowModal(true); }}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0F7D70] text-white rounded-lg hover:bg-[#0a5f55] transition-colors"
            data-testid="new-template-button"
          >
            <Plus size={20} />
            {t('templates.newTemplate')}
          </button>
        )}
      </div>
      
      {/* Search */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder={t('templates.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
          />
        </div>
      </div>
      
      {/* Templates Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F7D70]" />
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-100 text-center">
          <Files size={48} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">{t('templates.noTemplates')}</h3>
          <p className="text-gray-500 mb-4">
            {search 
              ? (uiLang === 'lt' ? 'Pagal jūsų paiešką nieko nerasta' : 'No results found')
              : t('templates.createFirst')}
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => (
            <div
              key={template.id}
              className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{template.name}</h3>
                  {template.description && (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{template.description}</p>
                  )}
                </div>
                {canEdit && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => { setEditingTemplate(template); setShowModal(true); }}
                      className="p-2 text-gray-400 hover:text-[#0F7D70] hover:bg-[#0F7D70]/10 rounded-lg transition-colors"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(template)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>
              
              <div className="space-y-2 text-sm mb-4">
                <div className="flex items-center justify-between text-gray-600">
                  <span>{uiLang === 'lt' ? 'Prekių' : 'Items'}:</span>
                  <span className="font-medium">{template.items?.length || 0}</span>
                </div>
                <div className="flex items-center justify-between text-gray-600">
                  <span>{t('common.total')}:</span>
                  <span className="font-medium text-[#0F7D70]">
                    {formatMoney(calculateTotal(template.items || []))}
                  </span>
                </div>
                {template.discount_pct > 0 && (
                  <div className="flex items-center justify-between text-gray-600">
                    <span>{uiLang === 'lt' ? 'Nuolaida' : 'Discount'}:</span>
                    <span className="font-medium text-red-500">{template.discount_pct}%</span>
                  </div>
                )}
              </div>
              
              <button
                onClick={() => handleUseTemplate(template)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#14233F] text-white rounded-lg hover:bg-[#1a3050] transition-colors"
              >
                <Copy size={16} />
                {t('templates.useTemplate')}
              </button>
            </div>
          ))}
        </div>
      )}
      
      {/* Modal */}
      {showModal && (
        <TemplateModal
          template={editingTemplate}
          onClose={() => setShowModal(false)}
          onSaved={() => {
            setShowModal(false);
            loadTemplates();
          }}
        />
      )}
    </div>
  );
};

// Template Modal
const TemplateModal = ({ template, onClose, onSaved }) => {
  const { t, language: uiLang } = useLanguage();
  const [form, setForm] = useState({
    name: template?.name || '',
    description: template?.description || '',
    entity_id: template?.entity_id || 'uab',
    language: template?.language || 'lt',
    items: template?.items || [],
    discount_pct: template?.discount_pct || 0,
    vat_rate: template?.vat_rate || 21,
    validity_days: template?.validity_days || 90,
    conditions: template?.conditions || ''
  });
  const [products, setProducts] = useState([]);
  const [entities, setEntities] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showCatalog, setShowCatalog] = useState(false);
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      const [productsRes, entitiesRes] = await Promise.all([
        getProducts(),
        getEntities()
      ]);
      setProducts(productsRes.data.filter(p => p.active !== false));
      setEntities(entitiesRes.data);
    } catch (error) {
      toast.error(getApiError(error));
    }
  };
  
  const addProduct = (product) => {
    const existing = form.items.find(i => i.ref === product.ref);
    if (existing) {
      setForm({
        ...form,
        items: form.items.map(i => 
          i.ref === product.ref 
            ? { ...i, quantity: i.quantity + 1, total: (i.quantity + 1) * i.unit_price }
            : i
        )
      });
    } else {
      setForm({
        ...form,
        items: [...form.items, {
          ref: product.ref,
          name: product.name,
          quantity: 1,
          unit_price: product.price,
          total: product.price
        }]
      });
    }
  };
  
  const updateItem = (index, field, value) => {
    const newItems = [...form.items];
    newItems[index][field] = value;
    
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].total = newItems[index].quantity * newItems[index].unit_price;
    }
    
    setForm({ ...form, items: newItems });
  };
  
  const removeItem = (index) => {
    setForm({ ...form, items: form.items.filter((_, i) => i !== index) });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name.trim()) {
      toast.error(t('templates.messages.nameRequired'));
      return;
    }
    
    if (form.items.length === 0) {
      toast.error(uiLang === 'lt' ? 'Pridėkite bent vieną prekę' : 'Add at least one item');
      return;
    }
    
    setSaving(true);
    try {
      if (template) {
        await updateTemplate(template.id, form);
        toast.success(t('templates.messages.updated'));
      } else {
        await createTemplate(form);
        toast.success(t('templates.messages.created'));
      }
      onSaved();
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setSaving(false);
    }
  };
  
  const productsByCategory = CATEGORIES.reduce((acc, cat) => {
    acc[cat] = products.filter(p => p.category === cat);
    return acc;
  }, {});
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Files size={20} className="text-[#0F7D70]" />
            {template 
              ? (uiLang === 'lt' ? 'Redaguoti šabloną' : 'Edit Template')
              : t('templates.newTemplate')}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="flex-1 overflow-auto p-4">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Left column - Form */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('templates.form.name')} *
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={uiLang === 'lt' ? 'Pvz.: Standartinis pasiūlymas bibliotekoms' : 'E.g.: Standard library quote'}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('templates.form.description')}
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('templates.form.entity')}
                  </label>
                  <select
                    value={form.entity_id}
                    onChange={(e) => setForm({ ...form, entity_id: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
                  >
                    {entities.map(entity => (
                      <option key={entity.id} value={entity.id}>{entity.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('templates.form.language')}
                  </label>
                  <select
                    value={form.language}
                    onChange={(e) => setForm({ ...form, language: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
                  >
                    <option value="lt">Lietuvių</option>
                    <option value="lv">Latviešu</option>
                    <option value="et">Eesti</option>
                    <option value="en">English</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('templates.form.discountPct')}
                  </label>
                  <input
                    type="number"
                    value={form.discount_pct}
                    onChange={(e) => setForm({ ...form, discount_pct: parseFloat(e.target.value) || 0 })}
                    min="0"
                    max="100"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('templates.form.vatRate')}
                  </label>
                  <input
                    type="number"
                    value={form.vat_rate}
                    onChange={(e) => setForm({ ...form, vat_rate: parseFloat(e.target.value) || 21 })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('templates.form.validityDays')}
                  </label>
                  <input
                    type="number"
                    value={form.validity_days}
                    onChange={(e) => setForm({ ...form, validity_days: parseInt(e.target.value) || 90 })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('templates.form.conditions')}
                </label>
                <textarea
                  value={form.conditions}
                  onChange={(e) => setForm({ ...form, conditions: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
                />
              </div>
            </div>
            
            {/* Right column - Items */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-900">{t('templates.form.items')}</h3>
                <button
                  type="button"
                  onClick={() => setShowCatalog(!showCatalog)}
                  className="text-sm text-[#0F7D70] hover:underline flex items-center gap-1"
                >
                  <Package size={16} />
                  {showCatalog 
                    ? (uiLang === 'lt' ? 'Slėpti katalogą' : 'Hide catalog')
                    : (uiLang === 'lt' ? 'Rodyti katalogą' : 'Show catalog')}
                </button>
              </div>
              
              {/* Items list */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                {form.items.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    {uiLang === 'lt' ? 'Pridėkite prekių iš katalogo' : 'Add items from catalog'}
                  </div>
                ) : (
                  <div className="max-h-48 overflow-y-auto">
                    {form.items.map((item, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 border-b border-gray-100 last:border-b-0">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{item.ref}</p>
                          <p className="text-xs text-gray-500 truncate">{item.name}</p>
                        </div>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                          min="1"
                          className="w-16 px-2 py-1 text-sm border border-gray-300 rounded text-center"
                        />
                        <span className="text-sm text-gray-600 w-20 text-right">
                          {formatMoney(item.total)}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="p-1 text-red-400 hover:text-red-600"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Catalog */}
              {showCatalog && (
                <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                  {CATEGORIES.map(category => {
                    const categoryProducts = productsByCategory[category];
                    if (!categoryProducts?.length) return null;
                    
                    return (
                      <div key={category}>
                        <div className="px-3 py-1.5 bg-gray-50 text-xs font-medium text-gray-500 uppercase sticky top-0">
                          {category}
                        </div>
                        {categoryProducts.map(product => (
                          <button
                            key={product.id}
                            type="button"
                            onClick={() => addProduct(product)}
                            className="w-full px-3 py-2 text-left hover:bg-[#0F7D70]/5 transition-colors border-b border-gray-50"
                          >
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-gray-900">{product.ref}</span>
                              <span className="text-sm text-[#0F7D70]">{formatMoney(product.price)}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </form>
        
        <div className="flex gap-3 p-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 px-4 py-2.5 bg-[#0F7D70] text-white rounded-lg hover:bg-[#0a5f55] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? t('common.loading') : (
              <>
                <Save size={18} />
                {template ? t('common.save') : t('common.create')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QdTemplates;
